import * as Yup from 'yup';
import { useMemo } from 'react';
import { enqueueSnackbar } from 'notistack';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';

import { DatePicker } from '@mui/x-date-pickers';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';

import { useAppDispatcher } from 'src/redux/store';
import { markChequeReturned } from 'src/redux/slices/cheque-factoring';

import FormProvider from 'src/components/hook-form';

import { IChequeItem } from 'src/types/cheque';

interface IMarkReturnedDialog {
  cheque: IChequeItem;
  isOpen: boolean;
  onClose: VoidFunction;
  onSuccess: VoidFunction;
}

function MarkReturnedDialog({ cheque, isOpen, onClose, onSuccess }: IMarkReturnedDialog) {
  const dispatch = useAppDispatcher();

  const schema = useMemo(
    () =>
      Yup.object().shape({
        returnedDate: Yup.date().required('Returned date is required'),
      }),
    []
  );

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: { returnedDate: new Date() },
    mode: 'onChange',
  });

  const {
    handleSubmit,
    control,
    formState: { isSubmitting },
    reset,
  } = methods;

  const onSubmit = handleSubmit(async (data) => {
    try {
      await dispatch(
        markChequeReturned({
          chequeId: cheque._id,
          returnedDate: new Date(data.returnedDate),
        })
      ).unwrap();

      enqueueSnackbar('Cheque marked as returned — settle with LOLC within 24 hours');
      reset();
      onSuccess();
      onClose();
    } catch (e) {
      if (typeof e === 'string') {
        enqueueSnackbar(e, { variant: 'error' });
      } else {
        enqueueSnackbar(e?.message || 'Failed to mark cheque as returned', {
          variant: 'error',
        });
      }
    }
  });

  return (
    <Dialog fullWidth maxWidth="xs" open={isOpen} onClose={onClose}>
      <DialogTitle sx={{ pb: 2 }}>Mark Returned</DialogTitle>
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <DialogContent sx={{ typography: 'body2' }}>
          <Typography sx={{ mb: 2 }} variant="body2">
            Confirm the date cheque <strong>{cheque.chequeNumber}</strong> bounced.
          </Typography>

          <Alert severity="warning" sx={{ mb: 2 }}>
            This must be settled with LOLC within 24 hours of the returned date.
          </Alert>

          <Stack spacing={2}>
            <Controller
              name="returnedDate"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <DatePicker
                  label="Returned Date"
                  disableFuture
                  value={field.value}
                  onChange={(newValue) => {
                    field.onChange(newValue);
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!error,
                      helperText: error?.message,
                    },
                  }}
                />
              )}
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" color="inherit" onClick={onClose}>
            Cancel
          </Button>

          <LoadingButton type="submit" variant="contained" color="error" loading={isSubmitting}>
            Confirm Returned
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}

export default MarkReturnedDialog;
